import type { AddMessageInput, ConceptOrigin } from "@mattoboard/schemas";

// Request bodies for creating a concept: the PENDING /addMessage input and the
// /api/design-stream/chat body. Shared by the web create flow
// (useCreateConceptWithAddMessage) and the SketchUp panel's Renders tab so the
// two can't drift. Key order is part of the contract: the web bodies are
// locked by inline snapshots in src/tests/design_stream_evt/useCreateConcept.test.ts.

export interface ConceptImageInput {
  url: string;
  tag?: string;
  viewpointUrl?: string;
}

export type PaletteColorInput = { l: number; a: number; b: number };

export interface BoardDataInput {
  materials: Array<{
    name: string;
    imageUrl?: string;
    categories?: string[];
    applicationTypes?: string[];
  }>;
}

/**
 * Image Edit "Render a 3D Model / Drawing": the backend renders the
 * upload photoreal (1 credit) before the normal upload flow continues. Only
 * sent alongside `useUploadFlow`.
 */
export interface SketchRenderRequest {
  kind: "sketchup" | "drawing";
  /** Render prompt options (both kinds). */
  options?: {
    type?: "interior" | "exterior";
    /** Time-of-day lighting ("Lighting" in the UI). */
    mood?: "day" | "sunset" | "night";
    /** Designer notes added to the prompt (≤500 chars). */
    instructions?: string;
    /** Legacy, ignored by the prompt. Still sent because the pre-Oct-2026
     *  backend requires it; remove once the optional-field schema is live. */
    lighting?: "sunlight" | "overcast";
  };
  /** SketchUp panel only: one of SKETCH_RENDER_ASPECT_RATIOS. */
  aspectRatio?: string;
  /** SketchUp panel only (SketchRenderSourceSchema). Never a file name. */
  source?: {
    app: "sketchup";
    modelId: string;
    sceneName?: string;
    camera?: {
      eye: [number, number, number];
      target: [number, number, number];
      up: [number, number, number];
      fov: number;
      perspective: boolean;
      aspectRatio: number;
    };
    extensionVersion: string;
  };
  /** SketchUp panel only: Mattoboard product IDs in the model (≤200). */
  productIds?: string[];
}

/** The PENDING /addMessage input that creates a concept. */
export function buildCreateConceptInput({
  brief,
  images,
  paletteColors,
  designFirmId,
  isUpload,
  projectId,
  origin,
  sourceModelId,
}: {
  brief: string;
  images: ConceptImageInput[];
  paletteColors: PaletteColorInput[];
  designFirmId: string | null;
  isUpload: boolean;
  projectId?: string | null;
  /** SketchUp panel renders: stamped on the concept at creation. */
  origin?: ConceptOrigin;
  sourceModelId?: string;
}): AddMessageInput {
  // Create a short preview name from the brief (first few words, max 30 chars)
  const previewName = isUpload
    ? "Uploaded image"
    : brief.split(/\s+/).slice(0, 4).join(" ").slice(0, 30);
  return {
    ...(isUpload ? { source: "UPLOAD" } : {}),
    // Stamp the concept's immutable wallet HERE — this is the
    // authoritative value the backend charges against. The `designFirmId`
    // sent to /chat is ignored for an existing concept (addMessageController
    // reads the concept's own stamp), so stamping only the chat request
    // would charge Personal when gated. See useCreditWorkspace.
    // (`as any`: design_firm_id is a backend-honored field not in the
    // AddMessageInput type — same pattern as useEnhance / useConceptActions.)
    ...({ design_firm_id: designFirmId } as any),
    ...(origin ? { origin } : {}),
    ...(sourceModelId ? { source_model_id: sourceModelId } : {}),
    message: {
      role: "user",
      content: [
        { type: "input_text", text: brief },
        ...images.map((img) => ({
          type: "input_image" as const,
          image_url: img.url,
          ...(img.tag ? { use: img.tag } : {}),
        })),
        ...(paletteColors.length > 0
          ? [{ type: "input_palette" as const, colors: paletteColors }]
          : []),
      ],
      state: "PENDING",
    },
    concept_version: {
      // For upload flow, the image already exists on Firebase — set it on
      // the PENDING version so concept.current.img_url is populated
      // immediately. This prevents useEditorGuard from bouncing the user
      // out of /home/concepts2/editor/{id} to the detail page while
      // uploadGraph is still enriching metadata in the background.
      img_url: isUpload ? (images[0]?.url ?? null) : null,
      action_type: isUpload ? "UPLOAD" : "LLM_GENERATION",
      name: previewName,
      // Project this concept was created inside (issue #3669). Set on
      // the initial version so it rides PRESERVED_VERSION_FIELDS through
      // every later backend write.
      ...(projectId ? { project_id: projectId } : {}),
    },
  } as AddMessageInput;
}

/** The /api/design-stream/chat body that runs a concept's generation. */
export function buildChatRequestBody({
  conceptId,
  brief,
  images,
  paletteColors,
  designFirmId,
  boardData,
  useUploadFlow,
  sketchRender,
}: {
  conceptId: string;
  brief: string;
  images: ConceptImageInput[];
  paletteColors: PaletteColorInput[];
  designFirmId: string | null;
  boardData?: BoardDataInput;
  useUploadFlow?: boolean;
  sketchRender?: SketchRenderRequest;
}) {
  return {
    conceptId,
    messages: [{ role: "user", content: brief }],
    images,
    paletteColors,
    designFirmId,
    ...(boardData?.materials?.length ? { boardData } : {}),
    ...(useUploadFlow
      ? { useUploadFlow: true, currentImageUrl: images[0]?.url }
      : {}),
    ...(useUploadFlow && sketchRender ? { sketchRender } : {}),
  };
}
